import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Columns3 } from "lucide-react";
import { countryName, flattenRegionOptions } from "@/lib/countries";

// PATCH 28 BW9 + BW8 — compare 2-3 markets side by side for one sector. 100% lookups; no AI, no
// firm quote. Local Market Readiness is shown as a coverage depth, never the CRX.
// Patch 59 — country/region order comes from the SHARED flattenRegionOptions (same source as the
// jurisdiction picker), so the two can no longer drift out of sync.
export default function CompareMarkets() {
  const [domains, setDomains] = useState([]);
  const [regions, setRegions] = useState([]);
  const [domain, setDomain] = useState("");
  const [picks, setPicks] = useState(["GB", "KE", "IN"]);
  const [res, setRes] = useState(null);

  useEffect(() => {
    api.get("/public/sector-domains").then(r => setDomains(r.data.domains || [])).catch(() => {});
    api.get("/public/region-taxonomy").then(r => setRegions(flattenRegionOptions(r.data.taxonomy))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!domain) { setRes(null); return; }
    api.get("/public/compare-markets", { params: { domain, jurs: picks.join(",") } })
      .then(r => setRes(r.data)).catch(() => setRes(null));
  }, [domain, picks]);

  const setPick = (i, v) => setPicks(p => p.map((x, j) => (j === i ? v : x)));

  return (
    <section style={{ margin: "0 auto", padding: "8px 24px 48px" }}>
      <div className="k-container" data-testid="compare-markets" style={{ padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <Columns3 size={18} color="var(--accent)" />
          <h3 className="k-heading" style={{ fontSize: 17, margin: 0 }}>Compare markets</h3>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <select className="k-control" data-testid="compare-sector" style={{ width: "auto", flex: "1 1 200px" }}
            value={domain} onChange={e => setDomain(e.target.value)}>
            <option value="">Sector…</option>
            {domains.map(d => <option key={d.domain} value={d.domain}>{d.label}</option>)}
          </select>
          {picks.map((p, i) => (
            <select key={i} className="k-control" data-testid={`compare-region-${i}`} style={{ width: "auto", flex: "1 1 140px" }}
              value={p} onChange={e => setPick(i, e.target.value)}>
              {regions.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
            </select>
          ))}
        </div>
        {res && (
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${res.markets.length}, 1fr)`, gap: 10 }} data-testid="compare-grid">
            {res.markets.map(m => (
              <div key={m.code} className="k-control" data-testid={`compare-col-${m.code}`}>
                <div className="k-heading" style={{ fontSize: 14, fontWeight: 700 }}>{countryName(m.name || m.code)}</div>
                <div className="k-muted" style={{ fontSize: 13, marginTop: 6 }}>{Number(m.connector_count).toLocaleString("en-GB")} connectors</div>
                <div className="k-muted" style={{ fontSize: 13 }}>{m.layer_count} compliance layers</div>
                <div className="k-muted" style={{ fontSize: 12, marginTop: 6 }}>Kadana coverage: <b>{m.readiness.depth}</b></div>
                <div className="k-muted" style={{ fontSize: 12, marginTop: 6 }}>Indicative: GBP {Number(res.price_range.gbp_low).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}–GBP {Number(res.price_range.gbp_high).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
            ))}
          </div>
        )}
        {res && res.note && <p className="k-muted" style={{ fontSize: 11, marginTop: 10 }}>{res.note}</p>}
      </div>
    </section>
  );
}
